const requestAnimationFrame =
	window.requestAnimationFrame || window.webkitRequestAnimationFrame;
const cancelAnimationFrame =
	window.cancelAnimationFrame || window.webkitCancelAnimationFrame;
const is = {
	num(a) {
		return typeof a === 'number';
	},
	obj(a) {
		return a.toString() === '[object Object]';
	},
};

export default class Animate {
	request;
	currenttime;
	timestamp;
	progress;
	dir = 1;
	actions = {
		done: false,
		begun: false,
		paused: false,
		delayed: false,
		processing: false,
	};
	pendingID;
	pendings = [];
	options;
	callback;
	static create(...props) {
		return new Animate(...props);
	}
	/**
	 * options: number | { duration: number, ease?: func }
	 * calback: func | { process: func, start?: func, done?: func}
	 */
	constructor(options, callback) {
		this.options = is.num(options)
			? { duration: options, easing: (p) => p }
			: options;
		this.callback = !is.obj(callback) ? { process: callback } : callback;
		this.loop = this.loop.bind(this);
	}

	loop(reqeuststamp) {
		this.currenttime = reqeuststamp - this.timestamp;
		this.progress = this.options.easing(
			this.currenttime / this.options.duration
		);
		const isReverse = this.dir === -1;
		if (isReverse) {
			this.progress = 1 - this.progress;
		}
		const notYetExceeded = this.currenttime < this.options.duration;
		if (notYetExceeded) {
			this.callback.process.call(this, this.progress, this.currenttime);
			this.action('begun', false);
			this.request = requestAnimationFrame(this.loop);
		} else {
			this.action('done', true);
			this.action('processing', false);
			this.cancel();
			this.callback.process.call(this, isReverse ? 0 : 1, this.currenttime);
			if ('done' in this.callback) this.callback.done.call(this);
		}
	}

	action(name, value) {
		if (!value) return this.actions[name];
		this.actions[name] = value;
	}

	animating(isRequesting = true) {
		if (isRequesting) {
			this.request = requestAnimationFrame(this.loop);
		} else {
			cancelAnimationFrame(this.request);
		}
		return this;
	}

	addPending(delay) {
		this.pendings = [...this.pendings, { delay, callbacks: [] }];
	}

	addToPendingsCallback(method) {
		const lastPending = this.pendings.length - 1;
		this.pendings[lastPending].callbacks.push(method);
	}

	executeAllPending() {
		this.pendingID = setTimeout(() => {
			this.action('delayed', false);
			this.pendings.shift().callbacks.forEach((method) => this[method]());
			this.pendings.length && this.executeAllPending();
		}, this.pendings[0].delay);
	}

	delay(delay) {
		this.action('delayed', true);
		this.addPending(delay);
		const onlyExecuteInFirstPending = this.pendings.length === 1;
		if (onlyExecuteInFirstPending) this.executeAllPending();
		return this;
	}

	unmount() {
		this.animating(false);
		this.pendingID && clearTimeout(this.pendingID);
	}

	start() {
		if (this.pendings.length) {
			return this.addToPendingsCallback('start');
		}
		this.timestamp = performance.now();
		this.action('done', false);
		this.action('begun', true);
		this.action('processing', true);
		if ('start' in this.callback) this.callback.start.call(this);
		return this.animating();
	}

	cancel() {
		if (this.pendings.length) {
			return this.addToPendingsCallback('cancel');
		}
		return this.animating(false);
	}

	reverse() {
		if (this.pendings.length) {
			return this.addToPendingsCallback('reverse');
		}
		this.dir *= -1;
		return this;
	}

	pause() {
		if (this.pendings.length) {
			return this.addToPendingsCallback('pause');
		}
		this.action('paused', true);
		this.action('processing', false);
		return this.cancel();
	}

	play() {
		if (this.pendings.length) {
			return this.addToPendingsCallback('play');
		}
		if (this.action('done')) return this;
		this.action('paused', false);
		this.action('processing', true);
		this.timestamp = performance.now() - this.currenttime;
		return this.animating();
	}

	isReverse() {
		return this.dir === -1;
	}

	isDone() {
		return this.action('done');
	}

	isStarted() {
		return this.action('begun');
	}

	isPaused() {
		return this.action('paused');
	}

	isProcessing() {
		return this.action('processing');
	}
}

// const useAnimate = ({
// 	in: defaultState = false,
// 	duration = 300,
// 	animation,
// }) => {
// 	const [state, setState] = useState(defaultState);
// 	const [show, setShow] = useState(defaultState);
// 	const [mounted, setMounted] = useState(false);
// 	const ref = useRef(null);
// 	const toggle = useCallback(() => {
// 		setShow((prev) => !prev);
// 	}, []);

// 	useEffect(() => {
// 		const target = ref.current;
// 		if (!target) return;
// 		let cleanup;
// 		let { enter = {}, exit = {} } = animation;
// 		if (!mounted) {
// 			const hasAuto = Object.keys(enter).find((name) =>
// 				isDimensionAuto(name, enter[name])
// 			);
// 			if (hasAuto) {
// 				target.style.overflow = 'hidden';
// 				Object.keys(state ? enter : exit).forEach(
// 					(prop) => (target.style[prop] = (show ? enter : exit)[prop])
// 				);
// 			}
// 			setMounted(true);
// 		}
// 		if (show && mounted) {
// 			cleanup = Animate.create(duration, {
// 				start() {
// 					setState(true);
// 					this.data = getStyleRange(enter, exit, target, show);
// 				},
// 				process(progress) {
// 					this.data.forEach((prop) => {
// 						const { name, base, range, suffix } = prop;
// 						target.style[name] = `${base + range * progress}${suffix || ''}`;
// 						console.log(prop);
// 					});
// 				},
// 			}).start();
// 		}

// 		if (!show && state) {
// 			cleanup = Animate.create(duration, {
// 				start() {
// 					this.data = getStyleRange(enter, exit, target, show);
// 				},
// 				process(progress) {
// 					this.data.forEach((prop) => {
// 						const { name, base, range, suffix } = prop;
// 						target.style[name] = `${base + range * progress}${suffix || ''}`;
// 					});
// 				},
// 				done() {
// 					setState(false);
// 				},
// 			})
// 				.reverse()
// 				.start();
// 		}

// 		return () => cleanup && cleanup.unmount();
// 	}, [show, state, mounted, duration, animation, ref]);

// 	return [ref, state || show, toggle];
// };

// const getStyleRange = (a, b, target, isEnter) => {
// 	const parseA = parseStyle(a, target);
// 	const parseB = parseStyle(b, target);
// 	const compute = window.getComputedStyle(target);
// 	return Object.keys(parseA).map((name) => {
// 		const [, valA = 0, suffix] = parseA[name] || [];
// 		const [, valB = 0] = parseB[name] || [];
// 		const [, base = 0] = compute[name]
// 			? parseProps(name, compute[name], target)
// 			: [];
// 		return {
// 			name,
// 			base: parseFloat(valB),
// 			range:
// 				parseFloat(valA) -
// 				parseFloat(valB) -
// 				(isEnter
// 					? parseFloat(base) - parseFloat(valB)
// 					: parseFloat(valA) - parseFloat(base)),
// 			suffix,
// 		};
// 	});
// };

// const isDimensionAuto = (name, value) =>
// 	/([hH]eight|[wW]idth)/.test(name) && value === 'auto';

// const parseProps = (name, value, target) => {
// 	const rexp = `(\\-?\\d{1,100})(px|rem|em|vh|%${
// 		/^(?:scale|opacity|scrollTop)/.test(name)
// 			? ')?'
// 			: /^(?:rotate|skew)/.test(name)
// 			? '|deg)'
// 			: ')?'
// 	}`;
// 	return isDimensionAuto(name, value)
// 		? [0, target.scrollHeight, 'px']
// 		: value.toString().match(new RegExp(rexp));
// };

// const parseStyle = (style, target) =>
// 	Object.keys(style).reduce((result, name) => {
// 		const value = style[name];
// 		const isDisplay = name === 'display';
// 		if (isDisplay) return result;

// 		result[name] = parseProps(name, value, target);

// 		return result;
// 	}, {});
